import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { Plus } from "lucide-react";
import { useState } from "react";
import { TiposAtendimento } from "./interface/TipoAtendimento.interface";
import TipoAtendimentoEdit from "./tipo_atendimento-edit";
import TipoAtendimentoList from "./tipo_atendimento-list";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TipoAtendimentoPage = () => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [editingTipoAtendimento, setEditingTipoAtendimento] = useState<TiposAtendimento | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tipoAtendimentoToDelete, setTipoAtendimentoToDelete] = useState<number | null>(null);

  const handleEdit = (tipoAtendimento: TiposAtendimento) => {
    setEditingTipoAtendimento(tipoAtendimento);
    setOpen(true);
  };

  const handleDelete = (id: number) => {
    setTipoAtendimentoToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!tipoAtendimentoToDelete) return;

    try {
      await api.delete(`/api/tipo_atendimento/${tipoAtendimentoToDelete}`);
      toast({
        title: "Sucesso",
        description: "Tipo de atendimento excluído com sucesso!",
      });
      // Recarrega a lista de tipos de atendimento
      setRefreshTrigger(prev => prev + 1);
      setDeleteDialogOpen(false);
      setTipoAtendimentoToDelete(null);
    } catch (error: any) {
      console.error("Erro ao excluir tipo de atendimento:", error);
      toast({
        title: "Erro",
        description: error.response?.data?.message || "Erro ao excluir tipo de atendimento",
        variant: "destructive",
      });
    }
  };

  const handleSuccess = () => {
    // Recarrega a lista de tipos de atendimento
    setRefreshTrigger(prev => prev + 1);
    setOpen(false);
    setEditingTipoAtendimento(null);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground">Tipos de Atendimento</h2>
            <p className="text-muted-foreground mt-1">Gerencie os tipos de atendimento disponíveis</p>
          </div>
          <Button
            className="gap-2"
            onClick={() => {
              setEditingTipoAtendimento(null);
              setOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Novo Tipo de Atendimento
          </Button>
        </div>

        <TipoAtendimentoList
          onEdit={handleEdit}
          onDelete={handleDelete}
          refreshTrigger={refreshTrigger}
        />

        <TipoAtendimentoEdit
          open={open}
          onOpenChange={setOpen}
          tipoAtendimento={editingTipoAtendimento}
          onSuccess={handleSuccess}
        />

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir este tipo de atendimento? Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setTipoAtendimentoToDelete(null)}>
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default TipoAtendimentoPage;
